import { useEffect, useState } from 'react';
import { ResourcePermission, ResourcePermissionRole, TenantShare } from '../types';
import {
  getSpecialistShares,
  addSpecialistShare,
  deleteSpecialistShare,
  deleteSpecialistGroupShare,
  updateSpecialistShare,
  getSpecialistMaxShareLevel
} from '../api';
import type { GroupShareInfo } from 'src/components/ui/ShareModal';

interface UseSpecialistShareProps {
  specialistId?: string;
  permission?: ResourcePermissionRole;
}

const useSpecialistShare = ({ specialistId, permission }: UseSpecialistShareProps) => {
  const [existingShares, setExistingShares] = useState<ResourcePermission[]>([]);
  const [groupShares, setGroupShares] = useState<GroupShareInfo[]>([]);
  const [tenantShare, setTenantShare] = useState<TenantShare | null>(null);
  const [maxShareLevel, setMaxShareLevel] = useState<'private' | 'individual' | 'tenant' | 'public'>('public');
  const [shareRestrictionReason, setShareRestrictionReason] = useState<string | undefined>(undefined);
  const [eligibleEmails, setEligibleEmails] = useState<string[] | undefined>(undefined);
  const [eligibleGroupIds, setEligibleGroupIds] = useState<string[] | undefined>(undefined);
  const [sharesLoading, setSharesLoading] = useState(false);

  useEffect(() => {
    if (specialistId && permission && permission >= ResourcePermissionRole.SHARE) {
      const fetchShares = async () => {
        try {
          setSharesLoading(true);
          const [shares, levelInfo] = await Promise.all([
            getSpecialistShares(specialistId),
            getSpecialistMaxShareLevel(specialistId)
          ]);
          setExistingShares(shares.user_shares);
          setGroupShares(shares.group_shares ?? []);
          setTenantShare(shares.tenant_share);
          setMaxShareLevel(levelInfo.max_level);
          setShareRestrictionReason(levelInfo.restriction_reason);
          setEligibleEmails(levelInfo.eligible_user_emails ?? undefined);
          setEligibleGroupIds(levelInfo.eligible_group_ids ?? undefined);
        } catch (error) {
          console.error('Error fetching specialist shares:', error);
          setExistingShares([]);
          setGroupShares([]);
          setTenantShare(null);
        } finally {
          setSharesLoading(false);
        }
      };
      fetchShares();
    } else {
      setExistingShares([]);
      setGroupShares([]);
      setTenantShare(null);
    }
  }, [specialistId, permission]);

  const handleAddPeople = async (
    emailList: string[],
    permissionLevel: ResourcePermissionRole,
    message: string,
    groupIds?: string[]
  ) => {
    if (!specialistId) return;

    const result = await addSpecialistShare(specialistId, {
      emails: emailList.length > 0 ? emailList : undefined,
      permission: permissionLevel,
      message,
      group_ids: groupIds
    });
    setExistingShares([...existingShares, ...result.user_shares]);
  };

  const handleShareWithTenant = async (permissionLevel: ResourcePermissionRole) => {
    if (!specialistId) return;

    const result = await addSpecialistShare(specialistId, {
      share_with_tenant: true,
      permission: permissionLevel
    });
    if (result.tenant_share) {
      setTenantShare(result.tenant_share);
    }
  };

  const handleUpdateTenantShare = async (permissionLevel: ResourcePermissionRole) => {
    if (!specialistId || !tenantShare) return;

    await updateSpecialistShare(specialistId, tenantShare.permission_id, permissionLevel);
    setTenantShare({ ...tenantShare, role: permissionLevel });
  };

  const handleRemoveTenantShare = async () => {
    if (!specialistId || !tenantShare) return;

    await deleteSpecialistShare(specialistId, tenantShare.permission_id);
    setTenantShare(null);
  };

  const handleRemovePerson = async (permissionId: string) => {
    if (!specialistId) return;

    await deleteSpecialistShare(specialistId, permissionId);
    setExistingShares(existingShares.filter((share) => share.permission_id !== permissionId));
  };

  const handleUpdatePermission = async (permissionId: string, permissionLevel: ResourcePermissionRole) => {
    if (!specialistId) return;

    await updateSpecialistShare(specialistId, permissionId, permissionLevel);
    setExistingShares(
      existingShares.map((share) =>
        share.permission_id === permissionId ? { ...share, role: permissionLevel } : share
      )
    );
  };

  const handleRemoveGroupShare = async (groupId: string) => {
    if (!specialistId) return;
    await deleteSpecialistGroupShare(specialistId, groupId);
    setGroupShares(groupShares.filter((g) => g.group_id !== groupId));
  };

  return {
    sharesLoading,
    existingShares,
    groupShares,
    tenantShare,
    maxShareLevel,
    shareRestrictionReason,
    eligibleEmails,
    eligibleGroupIds,
    handleAddPeople,
    handleRemovePerson,
    handleUpdatePermission,
    handleRemoveGroupShare,
    handleShareWithTenant,
    handleUpdateTenantShare,
    handleRemoveTenantShare
  };
};

export default useSpecialistShare;
